import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  CreditCard, AlertTriangle, CheckCircle2, Eye, EyeOff, 
  Shield, ExternalLink, RefreshCw, Key, Loader2, Info,
  Wallet, Building2, Store
} from 'lucide-react';
import axios from 'axios';
import CloverSettings from './CloverSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentGatewaysSettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [stripePaymentMode, setStripePaymentMode] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalStripe, setOriginalStripe] = useState(null);
  
  // Stripe settings
  const [stripe, setStripe] = useState({
    publishable_key: '',
    secret_key: '',
    webhook_secret: '',
    test_mode: true,
    enabled: false
  });

  // PayPal settings
  const [paypal, setPaypal] = useState({
    client_id: '',
    client_secret: '',
    sandbox_mode: true,
    enabled: false
  });

  // Square settings
  const [square, setSquare] = useState({
    application_id: '',
    access_token: '',
    location_id: '',
    sandbox_mode: true,
    enabled: false
  });

  useEffect(() => {
    fetchSettings();
    fetchStripePaymentMode();
  }, []);

  const fetchStripePaymentMode = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/subscriptions/payment-mode`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripePaymentMode(response.data);
    } catch (error) {
      console.error('Error fetching Stripe payment mode:', error);
      setStripePaymentMode({ is_configured: false, mode: 'not_configured' });
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [settingsRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/clinic/billing-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/clinic/billing-settings/gateway-status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (settingsRes.data) {
        const stripeData = settingsRes.data.stripe || stripe;
        setStripe(stripeData);
        setOriginalStripe(JSON.stringify(stripeData));
        setPaypal(settingsRes.data.paypal || paypal);
        setSquare(settingsRes.data.square || square);
        setHasUnsavedChanges(false);
      }

      if (statusRes.data) {
        setGatewayStatus(statusRes.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Track unsaved changes for Stripe
  useEffect(() => {
    if (originalStripe !== null) {
      const currentStripeJson = JSON.stringify(stripe);
      setHasUnsavedChanges(currentStripeJson !== originalStripe);
    }
  }, [stripe, originalStripe]);

  const saveStripeSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.put(`${API_URL}/api/clinic/billing-settings/stripe`, stripe, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Check if there's a warning about key type mismatch
      if (response.data?.warning) {
        toast.warning(response.data.warning, { duration: 10000 });
      } else {
        toast.success('Stripe settings saved successfully');
      }

      setOriginalStripe(JSON.stringify(stripe));
      setHasUnsavedChanges(false);
      fetchSettings();
      fetchStripePaymentMode(); // Refresh the payment mode status
    } catch (error) {
      console.error('Error saving Stripe settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const savePayPalSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API_URL}/api/clinic/billing-settings/paypal`, paypal, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('PayPal settings saved successfully');

      fetchSettings();
    } catch (error) {
      console.error('Error saving PayPal settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveSquareSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API_URL}/api/clinic/billing-settings/square`, square, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Square settings saved successfully');

      fetchSettings();
    } catch (error) {
      console.error('Error saving Square settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleShowSecret = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const StatusBadge = ({ gateway }) => {
    if (!gatewayStatus || !gatewayStatus[gateway]) return null;
    const status = gatewayStatus[gateway];
    
    if (!status.configured) {
      return (
        <Badge variant="outline" className="bg-gray-100 text-gray-600">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Not Configured
        </Badge>
      );
    }
    
    if (!status.enabled) {
      return (
        <Badge variant="outline" className="bg-yellow-100 text-yellow-700">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Disabled
        </Badge>
      );
    }
    
    // Check for key type mismatch (Stripe only)
    if (gateway === 'stripe' && status.key_mode_mismatch) {
      return (
        <Badge variant="outline" className="bg-red-100 text-red-700">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Key Mismatch!
        </Badge>
      );
    }
    
    // Show actual key type for Stripe
    if (gateway === 'stripe' && status.actual_key_type) {
      return (
        <Badge variant="outline" className={status.actual_key_type === 'live' ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Active ({status.actual_key_type})
        </Badge>
      );
    }
    
    return (
      <Badge variant="outline" className="bg-green-100 text-green-700">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        Active ({status.mode})
      </Badge>
    );
  };

  // Warning banner for key type mismatch
  const KeyMismatchWarning = () => {
    if (!gatewayStatus?.stripe?.key_mode_mismatch) return null;
    
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-800">Key Type Mismatch Detected!</h4>
            <p className="text-sm text-red-700 mt-1">
              You have <strong>Test Mode disabled</strong> but are using <strong>TEST keys</strong> (sk_test_*, pk_test_*).
              Payments will still go to Stripe TEST environment.
            </p>
            <p className="text-sm text-red-700 mt-2">
              <strong>To process real payments:</strong> Replace your test keys with <strong>LIVE keys</strong> from your Stripe Dashboard (sk_live_*, pk_live_*).
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Prominent Stripe Payment Mode Banner
  const StripePaymentModeBanner = () => {
    if (!stripePaymentMode) return null;

    // Not configured
    if (!stripePaymentMode.is_configured) {
      return (
        <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-200 rounded-full">
              <AlertTriangle className="h-6 w-6 text-gray-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-gray-800 text-lg">Stripe Not Configured</h4>
              <p className="text-sm text-gray-600">
                No Stripe API keys are configured. Payments cannot be processed until you add your Stripe keys below.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // TEST Mode - Show prominent warning
    if (stripePaymentMode.is_test_mode) {
      return (
        <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 mb-6 shadow-md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-200 rounded-full animate-pulse">
              <AlertTriangle className="h-6 w-6 text-amber-700" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-amber-800 text-lg">⚠️ TEST MODE ACTIVE</h4>
                <Badge className="bg-amber-500 text-white">TEST</Badge>
              </div>
              <p className="text-sm text-amber-700 mt-1">
                <strong>Real payments are NOT being processed.</strong> All transactions go to Stripe's test environment.
              </p>
              <p className="text-sm text-amber-700 mt-2">
                To accept real payments, replace your test keys (sk_test_*, pk_test_*) with <strong>LIVE keys</strong> (sk_live_*, pk_live_*) from your{' '}
                <a 
                  href="https://dashboard.stripe.com/apikeys" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline font-semibold hover:text-amber-900"
                >
                  Stripe Dashboard
                </a>.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // LIVE Mode - Show success confirmation
    if (stripePaymentMode.is_live_mode) {
      return (
        <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-200 rounded-full">
              <CheckCircle2 className="h-6 w-6 text-green-700" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-green-800 text-lg">✓ LIVE MODE ACTIVE</h4>
                <Badge className="bg-green-600 text-white">LIVE</Badge>
              </div>
              <p className="text-sm text-green-700 mt-1">
                <strong>Real payments are being processed.</strong> All transactions will charge actual money to your patients.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Unsaved Changes Warning Banner
  const UnsavedChangesWarning = () => {
    if (!hasUnsavedChanges) return null;
    
    return (
      <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-4 mb-4 shadow-md animate-pulse">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-orange-800">
              ⚠️ You have unsaved changes to your Stripe configuration!
            </p>
            <p className="text-xs text-orange-700 mt-1">
              Click "Save Stripe Settings" below to apply your changes, otherwise they will be lost.
            </p>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={saveStripeSettings}
            disabled={saving}
            className="border-orange-400 text-orange-700 hover:bg-orange-100"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Now'}
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Unsaved Changes Warning - Top Priority */}
      <UnsavedChangesWarning />
      
      {/* Stripe Payment Mode Banner - Most Important */}
      <StripePaymentModeBanner />
      
      {/* Key Mismatch Warning */}
      <KeyMismatchWarning />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Gateways</h1>
          <p className="text-gray-500 mt-1">
            Configure your payment gateways to process patient transactions
          </p>
        </div>
        <Button variant="outline" onClick={fetchSettings} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Explanation Banner - When to use this vs Stripe Connect */}
      <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Key className="h-5 w-5 text-teal-600" />
            </div>
            <div className="text-sm text-teal-900">
              <p className="font-semibold">When to use Payment Gateways?</p>
              <p className="mt-1 text-teal-800">
                Use this option if you <strong>already have a Stripe, PayPal or Square account</strong> with your own 
                API keys. Payments will go <strong>directly to your account</strong> without intermediaries or additional 
                platform fees.
              </p>
              <p className="mt-2 text-teal-700">
                💡 <strong>Alternative:</strong> If you don't have a Stripe account or prefer a simpler setup, 
                use <strong>Settings → Stripe Connect</strong> to create an account and start receiving payments quickly 
                (includes 3% platform fee).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Integration Mode</p>
              <p className="mt-1">
                Configure your own API keys to process real payments. Without keys configured, 
                the system operates in <strong>demo mode</strong> that simulates transactions without processing real payments.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gateway Tabs */}
      <Tabs defaultValue="stripe" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="stripe" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Stripe
          </TabsTrigger>
          <TabsTrigger value="clover" className="flex items-center gap-2">
            <Store className="h-4 w-4" />
            Clover
          </TabsTrigger>
          <TabsTrigger value="paypal" className="flex items-center gap-2">
            <Wallet className="h-4 w-4" />
            PayPal
          </TabsTrigger>
          <TabsTrigger value="square" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Square
          </TabsTrigger>
        </TabsList>

        {/* Stripe Tab */}
        <TabsContent value="stripe">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <CreditCard className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle>Stripe</CardTitle>
                    <CardDescription>World-leading payment processor</CardDescription>
                  </div>
                </div>
                <StatusBadge gateway="stripe" />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Enable Stripe</Label>
                  <p className="text-sm text-gray-500">Activate Stripe payments</p>
                </div>
                <Switch
                  checked={stripe.enabled}
                  onCheckedChange={(checked) => setStripe({ ...stripe, enabled: checked })}
                />
              </div>

              {/* Key Type Detection - Auto-detect based on key prefix */}
              {stripe.secret_key && (
                <div className={`p-4 rounded-lg border-2 ${
                  stripe.secret_key.includes('sk_live') 
                    ? 'bg-green-50 border-green-400' 
                    : stripe.secret_key.includes('sk_test')
                    ? 'bg-amber-50 border-amber-400'
                    : 'bg-gray-50 border-gray-300'
                }`}>
                  <div className="flex items-center gap-3">
                    {stripe.secret_key.includes('sk_live') ? (
                      <>
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="font-medium text-green-800">🔴 LIVE Keys Detected</p>
                          <p className="text-sm text-green-700">Real payments WILL be processed. Customers will be charged.</p>
                        </div>
                      </>
                    ) : stripe.secret_key.includes('sk_test') ? (
                      <>
                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                        <div>
                          <p className="font-medium text-amber-800">⚠️ TEST Keys Detected</p>
                          <p className="text-sm text-amber-700">No real payments will be processed. For testing only.</p>
                          <p className="text-xs text-amber-600 mt-1">
                            To accept real payments, replace with LIVE keys from your{' '}
                            <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-semibold">
                              Stripe Dashboard
                            </a>
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="h-5 w-5 text-gray-600" />
                        <div>
                          <p className="font-medium text-gray-800">Unknown Key Format</p>
                          <p className="text-sm text-gray-600">Please verify your API keys are correct.</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* API Keys */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="stripe_pk">Publishable Key</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="stripe_pk"
                      type={showSecrets.stripe_pk ? 'text' : 'password'}
                      value={stripe.publishable_key}
                      onChange={(e) => setStripe({ ...stripe, publishable_key: e.target.value })}
                      placeholder="pk_live_... or pk_test_..."
                      className="pl-10 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1"
                      onClick={() => toggleShowSecret('stripe_pk')}
                    >
                      {showSecrets.stripe_pk ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stripe_sk">Secret Key</Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="stripe_sk"
                      type={showSecrets.stripe_sk ? 'text' : 'password'}
                      value={stripe.secret_key}
                      onChange={(e) => setStripe({ ...stripe, secret_key: e.target.value })}
                      placeholder="sk_live_... or sk_test_..."
                      className="pl-10 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1"
                      onClick={() => toggleShowSecret('stripe_sk')}
                    >
                      {showSecrets.stripe_sk ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stripe_wh">Webhook Secret (Optional)</Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="stripe_wh"
                      type={showSecrets.stripe_wh ? 'text' : 'password'}
                      value={stripe.webhook_secret}
                      onChange={(e) => setStripe({ ...stripe, webhook_secret: e.target.value })}
                      placeholder="whsec_..."
                      className="pl-10 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1"
                      onClick={() => toggleShowSecret('stripe_wh')}
                    >
                      {showSecrets.stripe_wh ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Help Link */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Don't have Stripe keys?{' '}
                  <a 
                    href="https://dashboard.stripe.com/apikeys" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:underline inline-flex items-center gap-1"
                  >
                    Get them here <ExternalLink className="h-3 w-3" />
                  </a>
                </p>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col sm:flex-row gap-2">
              <Button 
                onClick={saveStripeSettings} 
                disabled={saving}
                className={`w-full sm:w-auto ${hasUnsavedChanges ? 'bg-orange-500 hover:bg-orange-600 animate-pulse' : ''}`}
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                {hasUnsavedChanges ? '⚠️ Save Changes Now' : 'Save Stripe Settings'}
              </Button>
              {hasUnsavedChanges && (
                <span className="text-sm text-orange-600 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  Unsaved changes
                </span>
              )}
            </CardFooter>
          </Card>
        </TabsContent>

        {/* Clover Tab */}
        <TabsContent value="clover">
          <CloverSettings />
        </TabsContent>

        {/* PayPal Tab */}
        <TabsContent value="paypal">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Wallet className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle>PayPal</CardTitle>
                    <CardDescription>Secure payments with PayPal</CardDescription>
                  </div>
                </div>
                <StatusBadge gateway="paypal" />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Enable PayPal</Label>
                  <p className="text-sm text-gray-500">Activate PayPal payments</p>
                </div>
                <Switch
                  checked={paypal.enabled}
                  onCheckedChange={(checked) => setPaypal({ ...paypal, enabled: checked })}
                />
              </div>

              {/* Sandbox Mode */}
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div>
                  <Label className="font-medium text-amber-800">Sandbox Mode</Label>
                  <p className="text-sm text-amber-600">Use test environment</p>
                </div>
                <Switch
                  checked={paypal.sandbox_mode}
                  onCheckedChange={(checked) => setPaypal({ ...paypal, sandbox_mode: checked })}
                />
              </div>

              {/* API Keys */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="paypal_client">Client ID</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="paypal_client"
                      type={showSecrets.paypal_client ? 'text' : 'password'}
                      value={paypal.client_id}
                      onChange={(e) => setPaypal({ ...paypal, client_id: e.target.value })}
                      placeholder="Your PayPal Client ID"
                      className="pl-10 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1"
                      onClick={() => toggleShowSecret('paypal_client')}
                    >
                      {showSecrets.paypal_client ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paypal_secret">Client Secret</Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="paypal_secret"
                      type={showSecrets.paypal_secret ? 'text' : 'password'}
                      value={paypal.client_secret}
                      onChange={(e) => setPaypal({ ...paypal, client_secret: e.target.value })}
                      placeholder="Your PayPal Client Secret"
                      className="pl-10 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1"
                      onClick={() => toggleShowSecret('paypal_secret')}
                    >
                      {showSecrets.paypal_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Help Link */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Don't have PayPal credentials?{' '}
                  <a 
                    href="https://developer.paypal.com/dashboard/applications" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:underline inline-flex items-center gap-1"
                  >
                    Get them here <ExternalLink className="h-3 w-3" />
                  </a>
                </p>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={savePayPalSettings} 
                disabled={saving}
                className="w-full sm:w-auto"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Save PayPal Settings
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* Square Tab */}
        <TabsContent value="square">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Building2 className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle>Square</CardTitle>
                    <CardDescription>All-in-one payment solution</CardDescription>
                  </div>
                </div>
                <StatusBadge gateway="square" />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Enable Square</Label>
                  <p className="text-sm text-gray-500">Activate Square payments</p>
                </div>
                <Switch
                  checked={square.enabled}
                  onCheckedChange={(checked) => setSquare({ ...square, enabled: checked })}
                />
              </div>

              {/* Sandbox Mode */}
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div>
                  <Label className="font-medium text-amber-800">Sandbox Mode</Label>
                  <p className="text-sm text-amber-600">Use test environment</p>
                </div>
                <Switch
                  checked={square.sandbox_mode}
                  onCheckedChange={(checked) => setSquare({ ...square, sandbox_mode: checked })}
                />
              </div>

              {/* API Keys */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="square_app">Application ID</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="square_app"
                      type="text"
                      value={square.application_id}
                      onChange={(e) => setSquare({ ...square, application_id: e.target.value })}
                      placeholder="sq0idp-..."
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="square_token">Access Token</Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="square_token"
                      type={showSecrets.square_token ? 'text' : 'password'}
                      value={square.access_token}
                      onChange={(e) => setSquare({ ...square, access_token: e.target.value })}
                      placeholder="EAAAl..."
                      className="pl-10 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1"
                      onClick={() => toggleShowSecret('square_token')}
                    >
                      {showSecrets.square_token ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="square_location">Location ID</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="square_location"
                      type="text"
                      value={square.location_id}
                      onChange={(e) => setSquare({ ...square, location_id: e.target.value })}
                      placeholder="L..."
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* Help Link */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Don't have Square credentials?{' '}
                  <a 
                    href="https://developer.squareup.com/apps" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:underline inline-flex items-center gap-1"
                  >
                    Get them here <ExternalLink className="h-3 w-3" />
                  </a>
                </p>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={saveSquareSettings} 
                disabled={saving}
                className="w-full sm:w-auto"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Save Square Settings
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Demo Mode Warning - Only show if NO gateway is in LIVE mode */}
      {(!stripePaymentMode?.is_live_mode && !gatewayStatus?.stripe?.connected) && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Current Status: Demo Mode</p>
                <p className="mt-1">
                  The system is currently operating in <strong>demo mode</strong>. 
                  Cards added by patients are saved locally but no real payments are processed. 
                  To process real payments, configure at least one payment gateway with valid LIVE keys.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentGatewaysSettings;
